import { BAND_COLOUR, BAND_LABEL, badgeDate, type ScoreBand } from '@cyberscore/contracts';
import { Copy, Info, Link2, Share2, ShieldCheck, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../ui/Toasts';
import type { AssessmentSummary } from '../assessments/api';
import { badgeLinks, badgeSnippet, useBadge, useCanShare, useShareBadge } from './badge';

/** The badge as a card: what the public link shows, and the preview before sharing. */
export function BadgeCard(p: {
  business: string;
  score: number;
  band: ScoreBand;
  verified: number;
  date: string | null;
}) {
  return (
    <div className="badge-card">
      <div className="brand-mark big" aria-hidden>
        <ShieldCheck size={26} />
      </div>
      <div className="grow">
        <div className="eyebrow">CyberScore · verified {Math.round(p.verified * 100)}%</div>
        <div className="badge-score num">
          {p.score} <span style={{ color: BAND_COLOUR[p.band] }}>{BAND_LABEL[p.band].toUpperCase()}</span>
        </div>
        <div className="muted small">
          {p.business}
          {p.date ? ` · checked ${badgeDate(p.date)}` : ''}
        </div>
      </div>
    </div>
  );
}

export function CopyRow({
  id,
  label,
  value,
  what,
}: {
  id: string;
  label: string;
  value: string;
  what: string;
}) {
  const toast = useToast();
  const copy = () =>
    navigator.clipboard
      .writeText(value)
      .then(() => toast(`${what} copied`))
      .catch(() => {
        (document.getElementById(id) as HTMLInputElement | null)?.select();
        toast(`Select the ${what.toLowerCase()} and copy it`, 'bad');
      });
  return (
    <>
      <label className="eyebrow mt" htmlFor={id}>
        {label}
      </label>
      <div className="copy-row mt-s">
        <input id={id} className="inp" readOnly value={value} onFocus={(e) => e.currentTarget.select()} />
        <button type="button" className="bbtn" onClick={copy}>
          <Copy size={15} /> Copy
        </button>
      </div>
    </>
  );
}

/**
 * Share your CyberScore (the prototype's share dialog): a public link and a badge image for a website,
 * showing the latest complete score, its band and how much is verified. The owner turns it on and off.
 */
export function ShareScore({
  a,
  score,
  onClose,
}: {
  a: AssessmentSummary;
  score: { score: number; band: ScoreBand; verified: number; provisional: boolean; date: string | null };
  onClose: () => void;
}) {
  const view = useBadge(a.id);
  const share = useShareBadge(a.id);
  const owner = useCanShare();
  const toast = useToast();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Focus moves into the dialog once, when it opens.
  useEffect(() => ref.current?.querySelector<HTMLElement>('.modal-b button, .modal-f button')?.focus(), []);

  const live = view.data ?? null;
  const b = live?.badge;
  const run = (on: boolean) =>
    share.mutate(on, {
      onSuccess: () => toast(on ? 'Your score is shared' : 'Sharing stopped. The link no longer works.'),
      onError: (e) => toast(e.message, 'bad'),
    });

  // Above the top bar: the page sits in its own stacking layer, so the dialog goes to the app's root.
  return createPortal(
    <div className="modal-root" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="share-title" ref={ref}>
        <div className="modal-h">
          <div className="hicon">
            <Share2 size={18} />
          </div>
          <div className="grow">
            <h3 id="share-title">Share your CyberScore</h3>
            <div className="muted small">A live badge customers and suppliers can check</div>
          </div>
          <button type="button" className="icon-btn x" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        <div className="modal-b">
          {b ? (
            <BadgeCard
              business={b.business}
              score={b.score}
              band={b.band}
              verified={b.verified}
              date={b.scored_at}
            />
          ) : (
            <BadgeCard
              business={a.customer_name}
              score={score.score}
              band={score.band}
              verified={score.verified}
              date={score.date}
            />
          )}

          {view.isPending ? (
            <p className="muted mt">Loading…</p>
          ) : view.isError ? (
            <p className="bad-t mt">{view.error.message}</p>
          ) : live ? (
            <>
              <CopyRow id="share-link" label="Public link" value={badgeLinks(live.token).page} what="Link" />
              <label className="eyebrow mt">Badge for your website or email signature</label>
              <div className="badge-img mt-s">
                <img
                  src={badgeLinks(live.token).image}
                  alt={`CyberScore badge for ${a.customer_name}`}
                  width={300}
                  height={96}
                />
              </div>
              <CopyRow
                id="share-html"
                label="HTML to paste"
                value={badgeSnippet(live.token, a.customer_name)}
                what="HTML"
              />
            </>
          ) : score.provisional ? (
            <p className="note mt">
              <Info size={15} /> Finish your Cyber Check first: a badge shows a complete score.
            </p>
          ) : !owner ? (
            <p className="note mt">
              <Info size={15} /> Only the business owner can share the score.
            </p>
          ) : (
            <p className="muted mt">
              Make a public link anyone can open, without signing in. You can stop sharing at any time: the
              link stops working at once, and the badge image within a minute.
            </p>
          )}

          <p className="muted mt tiny-note">
            The badge shows your score, band and verification level only. Your answers, evidence and insurer
            stay private.
          </p>
        </div>

        <div className="modal-f">
          {live && owner && (
            <button type="button" className="bbtn" disabled={share.isPending} onClick={() => run(false)}>
              Stop sharing
            </button>
          )}
          {!live && owner && !score.provisional && !view.isPending && (
            <button type="button" className="bbtn pri" disabled={share.isPending} onClick={() => run(true)}>
              <Link2 size={15} /> {share.isPending ? 'Creating…' : 'Create public link'}
            </button>
          )}
          <button type="button" className="bbtn dark" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.querySelector('.biz') ?? document.body,
  );
}
